import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import RankTrustLogo from "@/components/RankTrustLogo";
import SEOHead from "@/lib/seo/SEOHead";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "agency" ? "agency" : "business";

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: initialRole,
  });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      if (u.role === "agency") nav("/app/agency");
      else nav("/onboarding");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <SEOHead path="/register" />
      <div className="hidden md:flex flex-col justify-between p-12 bg-zinc-950 text-white grid-bg">
        <div data-testid="register-brand"><RankTrustLogo height={36} asLink tagline taglineColor="#9CA3AF" /></div>
        <div>
          <h2 className="font-display text-4xl font-black tracking-tighter">
            Let's get your business in the<br /><span className="text-[#FACC15]">Top 3</span>.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-md">Free trial, no card required. Start ranking with intelligence.</p>
        </div>
        <div className="font-mono text-xs text-zinc-500">RANKTRUST · AI Growth Intelligence Platform</div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <div className="label-tiny mb-2">Get started</div>
            <h1 className="font-display text-3xl font-black tracking-tight">Create your account</h1>
          </div>
          <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200" data-testid="role-toggle">
            {["business","agency"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={()=>setForm({...form, role: r})}
                className={`p-3 text-xs uppercase tracking-widest font-bold ${form.role===r ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
                data-testid={`role-${r}`}
              >
                {r === "business" ? "I'm a business" : "I'm an agency"}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <label className="label-tiny block">Full name</label>
            <input className="input-field" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="register-name" />
          </div>
          <div className="space-y-2">
            <label className="label-tiny block">Email</label>
            <input className="input-field" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="register-email" />
          </div>
          <div className="space-y-2">
            <label className="label-tiny block">Password (min 6)</label>
            <input className="input-field" type="password" minLength={6} required value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} data-testid="register-password" />
          </div>
          <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="register-submit">
            {busy ? <span className="spinner"></span> : <>Create account <ArrowRight size={14} weight="bold" /></>}
          </button>
          <div className="text-sm text-zinc-600">
            Already have an account? <Link to="/login" className="text-[#002FA7] font-semibold" data-testid="register-to-login">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
